<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <h2>Synthetic Monitoring</h2>
      <v-btn small outlined @click="toggleAllSites">
        <v-icon left
          >{{
            allExpanded
              ? icons.mdiUnfoldLessHorizontal
              : icons.mdiUnfoldMoreHorizontal
          }}
        </v-icon>
        {{ allExpanded ? "Tout replier" : "Tout déplier" }}
      </v-btn>
    </v-card-title>
    <v-card-text>
      <!-- Headers Row -->
      <v-row class="ma-0 pa-2 mb-2">
        <v-col cols="5"></v-col>
        <v-col cols="7">
          <v-row class="ma-0">
            <v-col
              class="text-center pa-1"
              cols="auto"
              style="min-width: 100px"
            ></v-col>
            <v-spacer></v-spacer>
            <v-col class="text-center pa-1" style="width: 60px">
              <div class="text-caption text--secondary font-weight-bold">
                24H
              </div>
            </v-col>
            <v-col class="text-center pa-1" style="width: 60px">
              <div class="text-caption text--secondary font-weight-bold">
                7D
              </div>
            </v-col>
            <v-col class="text-center pa-1" style="width: 80px">
              <div class="text-caption text--secondary font-weight-bold">
                Interval
              </div>
            </v-col>
            <v-col class="text-center pa-1" style="width: 80px">
              <div class="text-caption text--secondary font-weight-bold">
                TTFB
              </div>
            </v-col>
            <v-col class="text-center pa-1" style="width: 80px">
              <div class="text-caption text--secondary font-weight-bold">
                Poids
              </div>
            </v-col>
            <v-col class="text-center pa-1" style="width: 80px">
              <div class="text-caption text--secondary font-weight-bold">
                DOM
              </div>
            </v-col>
            <v-col class="text-center pa-1" style="width: 60px">
              <div class="text-caption text--secondary font-weight-bold">
                Détails
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <div v-if="loading" class="text-center pa-4">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <div v-else>
        <!-- Site Groups -->
        <div
          v-for="siteGroup in urlMonitorStats"
          :key="siteGroup.site_name"
          class="mb-4"
        >
          <!-- Site Group Header -->
          <v-row class="align-center ma-0 pa-2 site-group-header">
            <v-col cols="auto" class="py-1">
              <v-btn icon small @click="toggleSiteGroup(siteGroup.site_name)">
                <v-icon>
                  {{
                    expandedSites.includes(siteGroup.site_name)
                      ? icons.mdiChevronDown
                      : icons.mdiChevronRight
                  }}
                </v-icon>
              </v-btn>
              <strong class="ml-2">{{ siteGroup.site_name }}</strong>
            </v-col>
            <v-spacer></v-spacer>
            <!-- Site Group Status Icons -->
            <v-col cols="auto" class="py-1">
              <div class="d-flex align-center">
                <hover-tooltip
                  v-for="(urlData, index) in siteGroup.urls"
                  :key="`icon-${index}`"
                  :tooltip-style="tooltipStyle"
                  :offset="-50"
                  class="d-inline-flex"
                >
                  <v-icon
                    :color="getUrlStatusColor(urlData)"
                    size="20"
                    class="ml-1"
                  >
                    {{ getUrlStatusIcon(urlData) }}
                  </v-icon>
                  <template v-slot:tooltip>
                    <div class="font-weight-bold">{{ urlData.title }}</div>
                    <div class="text-caption">{{ urlData.url }}</div>
                  </template>
                </hover-tooltip>
              </div>
            </v-col>
          </v-row>

          <!-- Site Group URLs (Expandable) -->
          <v-expand-transition>
            <div v-show="expandedSites.includes(siteGroup.site_name)">
              <div
                v-for="urlData in siteGroup.urls"
                :key="urlData.url"
                class="ml-4 mb-2"
              >
                <v-row class="align-center ma-0 pa-2 url-row">
                  <v-col cols="5" class="py-1 d-flex align-center">
                    <v-icon
                      :color="getUrlStatusColor(urlData)"
                      size="20"
                      class="mr-2"
                    >
                      {{ getUrlStatusIcon(urlData) }}
                    </v-icon>
                    <div class="mr-3" style="flex: 1">
                      <div class="font-weight-medium">{{ urlData.title }}</div>
                      <div class="text-caption text--secondary">
                        <a :href="urlData.url" target="_blank" class="url-link">
                          {{ truncateUrl(urlData.url) }}
                        </a>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="7" class="py-1">
                    <v-row class="ma-0 align-center">
                      <!-- Mini Chart -->
                      <v-col class="pa-1" cols="auto" style="min-width: 100px">
                        <ttfb-mini-chart :data="urlData.ttfb_last_30 || []" />
                      </v-col>
                      <v-spacer></v-spacer>
                      <!-- URL Stats aligned with headers -->
                      <v-col class="text-center pa-1" style="width: 60px">
                        <div
                          class="font-weight-medium"
                          :class="
                            getSuccessRateClass(urlData.success_rate_24h, true)
                          "
                        >
                          {{ urlData.success_rate_24h }}%
                        </div>
                      </v-col>
                      <v-col class="text-center pa-1" style="width: 60px">
                        <div
                          class="font-weight-medium"
                          :class="
                            getSuccessRateClass(urlData.success_rate_7d, false)
                          "
                        >
                          {{ urlData.success_rate_7d }}%
                        </div>
                      </v-col>
                      <v-col class="text-center pa-1" style="width: 80px">
                        <div class="font-weight-medium">
                          {{ urlData.interval_minutes }}
                        </div>
                      </v-col>
                      <v-col class="text-center pa-1" style="width: 80px">
                        <div
                          class="font-weight-medium"
                          :class="getTtfbClass(urlData.avg_ttfb_24h)"
                        >
                          {{ Math.round(urlData.avg_ttfb_24h) }}ms
                        </div>
                      </v-col>
                      <v-col class="text-center pa-1" style="width: 80px">
                        <div class="font-weight-medium">
                          {{ formatBytes(urlData.size_bytes) }}
                        </div>
                      </v-col>
                      <v-col class="text-center pa-1" style="width: 80px">
                        <div class="font-weight-medium">
                          {{ formatNumber(urlData.dom_elements_count) }}
                        </div>
                      </v-col>
                      <v-col class="text-center pa-1" style="width: 60px">
                        <v-btn
                          icon
                          small
                          @click="showExtendedHistory(urlData)"
                        >
                          <v-icon>{{ icons.mdiEye }}</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </v-card-text>
    
    <!-- Extended History Modal -->
    <url-monitor-history-modal
      v-if="showHistoryModal"
      :monitor="selectedMonitor"
      @close="showHistoryModal = false"
    />
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import {
  mdiChevronDown,
  mdiChevronRight,
  mdiCheckCircle,
  mdiCloseCircle,
  mdiAlertCircle,
  mdiUnfoldMoreHorizontal,
  mdiUnfoldLessHorizontal,
  mdiEye,
} from "@mdi/js";
import TtfbMiniChart from "@/components/web-perf/synthetic-monitoring/charts/TtfbMiniChart.vue";
import HoverTooltip from "@/components/common/HoverTooltip.vue";
import UrlMonitorHistoryModal from "@/components/web-perf/synthetic-monitoring/dashboard/UrlMonitorHistoryModal.vue";
import { formatNumber } from "@/utils/formatting";

export default {
  name: "UrlMonitorStatsTable",
  components: {
    TtfbMiniChart,
    HoverTooltip,
    UrlMonitorHistoryModal,
  },
  data() {
    return {
      loading: true,
      expandedSites: [],
      icons: {
        mdiChevronDown,
        mdiChevronRight,
        mdiCheckCircle,
        mdiCloseCircle,
        mdiAlertCircle,
        mdiUnfoldMoreHorizontal,
        mdiUnfoldLessHorizontal,
        mdiEye,
      },
      tooltipStyle: {
        backgroundColor: "#fff",
        color: "#333",
        padding: "8px 12px",
        borderRadius: "4px",
        fontSize: "12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
        maxWidth: "400px",
      },
      showHistoryModal: false,
      selectedMonitor: null,
    };
  },
  computed: {
    ...mapGetters("webPerf", ["getUrlMonitorStats"]),
    urlMonitorStats() {
      return this.getUrlMonitorStats;
    },
    allExpanded() {
      return (
        this.urlMonitorStats.length > 0 &&
        this.expandedSites.length === this.urlMonitorStats.length
      );
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    formatNumber,
    async loadData() {
      this.loading = true;
      try {
        await this.$store.dispatch("webPerf/getUrlMonitorStats");
        this.expandedSites = this.urlMonitorStats.map((site) => site.site_name);
      } catch (error) {
        console.error("Error loading URL monitor stats:", error);
        this.$store.dispatch("alerts/addAlert", {
          message: "Erreur lors du chargement des statistiques",
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    toggleSiteGroup(siteName) {
      const index = this.expandedSites.indexOf(siteName);
      if (index > -1) {
        this.expandedSites.splice(index, 1);
      } else {
        this.expandedSites.push(siteName);
      }
    },
    getUrlStatusColor(urlData) {
      if (
        urlData.last_status_code !== 200 ||
        urlData.success_rate_24h < 90 ||
        urlData.success_rate_7d < 90
      )
        return "error";
      if (
        urlData.success_rate_24h < 100 ||
        urlData.success_rate_7d < 99 ||
        urlData.avg_ttfb_24h > 800
      ) {
        return "warning";
      }
      return "success";
    },
    getUrlStatusIcon(urlData) {
      if (
        urlData.last_status_code !== 200 ||
        urlData.success_rate_24h < 90 ||
        urlData.success_rate_7d < 90
      )
        return this.icons.mdiCloseCircle;
      if (
        urlData.success_rate_24h < 100 ||
        urlData.success_rate_7d < 99 ||
        urlData.avg_ttfb_24h > 800
      ) {
        return this.icons.mdiAlertCircle;
      }
      return this.icons.mdiCheckCircle;
    },
    getSuccessRateClass(rate, is24h = false) {
      if (rate < 90) return "error--text";
      const threshold = is24h ? 100 : 99;
      if (rate < threshold) return "warning--text";
      return "success--text";
    },
    getTtfbClass(ttfb) {
      if (ttfb > 800) return "warning--text";
      return "";
    },
    formatBytes(bytes) {
      if (bytes === 0) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },
    truncateUrl(url) {
      if (!url) return "";
      if (url.length <= 70) return url;
      return url.substring(0, 70) + "...";
    },
    toggleAllSites() {
      if (this.allExpanded) {
        this.expandedSites = [];
      } else {
        this.expandedSites = this.urlMonitorStats.map((site) => site.site_name);
      }
    },
    showExtendedHistory(urlData) {
      this.selectedMonitor = urlData;
      this.showHistoryModal = true;
    },
  },
};
</script>

<style scoped>
.site-group-header {
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 8px;
}

.url-row {
  border-left: 3px solid #e0e0e0;
  margin-left: 12px;
  padding-left: 12px;
}

.text-caption {
  font-size: 0.75rem !important;
  line-height: 1;
}

.url-link {
  color: inherit;
  text-decoration: none;
}

.url-link:hover {
  text-decoration: underline;
}
</style>
